import React, { useState, useEffect, useMemo } from 'react';
import { useCRM } from '../context/CRMContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where,
} from 'firebase/firestore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const PLATFORM_TO_PROVIDER = { google_meet: 'google', teams: 'microsoft', zoom: 'zoom' };
const PROVIDER_LABEL = { google: 'Google', microsoft: 'Microsoft', zoom: 'Zoom' };
const PROVIDER_COLOR = { google: '#ea4335', microsoft: '#0078d4', zoom: '#2d8cff' };

const PLATFORMS = [
  { id: 'google_meet', label: 'Google Meet',     icon: '🎥', color: '#4285f4', autoLink: true },
  { id: 'teams',       label: 'Microsoft Teams', icon: '🟦', color: '#5059c9', autoLink: true },
  { id: 'zoom',        label: 'Zoom',            icon: '🔵', color: '#2d8cff', autoLink: true },
  { id: 'whatsapp',    label: 'WhatsApp Call',   icon: '📞', color: '#25d366', autoLink: false },
  { id: 'phone',       label: 'Phone Call',      icon: '☎️', color: '#64748b', autoLink: false },
  { id: 'in_person',   label: 'In Person',       icon: '🤝', color: '#f59e0b', autoLink: false },
  { id: 'other',       label: 'Other',           icon: '📅', color: '#94a3b8', autoLink: false },
];

const MEETING_STATUS = {
  scheduled:   { label: 'Scheduled',   color: '#0ea5e9' },
  completed:   { label: 'Completed',   color: '#10b981' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444' },
  no_show:     { label: 'No Show',     color: '#f59e0b' },
  rescheduled: { label: 'Rescheduled', color: '#8b5cf6' },
};

const MEETING_TYPES = ['Initial Consultation', 'Profile Assessment', 'Shortlisting Session',
  'Document Review', 'Application Review', 'Interview Prep', 'Visa Guidance', 'Follow-up', 'Other'];

const platformMap = Object.fromEntries(PLATFORMS.map(p => [p.id, p]));
const labelSt = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block' };

const generateMeetLink = () => {
  const r = () => Math.random().toString(36).substring(2, 8);
  return `https://meet.jit.si/${r()}-${r()}-${r()}`;
};

const formatDT = (dateStr, timeStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (timeStr ? 'T' + timeStr : ''));
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    (timeStr ? ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
};

const minutesUntil = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return Math.ceil((new Date(dateStr + 'T' + timeStr) - new Date()) / 60000);
};

// ─── Connected Accounts Hook (shared with LeadMeetingTab) ────────────────────
export function useOAuthStatus(companyId, userId) {
  const [status, setStatus] = useState(null);
  const refresh = () => {
    if (!companyId || !userId) return;
    fetch(`${BACKEND_URL}/api/oauth/status?${new URLSearchParams({ companyId, userId })}`)
      .then(r => r.json()).then(d => setStatus(d || {})).catch(() => {});
  };
  useEffect(() => { refresh(); }, [companyId, userId]); // eslint-disable-line
  return [status, refresh];
}

// ─── Connected Accounts Bar ───────────────────────────────────────────────────
function ConnectedAccountsBar({ currentUser, company, oauthStatus, onRefresh }) {
  const { firebaseUser } = useAuth();
  const [connecting, setConnecting] = useState(null);
  const [waking, setWaking] = useState(false);

  const handleConnect = async (provider) => {
    if (!company?.id || !currentUser?.id) return;
    setConnecting(provider);
    const provId = provider === 'google' ? 'google.com' : 'microsoft.com';
    // Only hint the email when Firebase providerData has a matching entry for THIS provider.
    // Without this guard, a Microsoft-SSO user clicking "Connect Google" would have their
    // company-domain email passed as login_hint → Google pre-selects the wrong account.
    const loginEmail = firebaseUser?.providerData?.find(p => p.providerId === provId)?.email;
    const params = new URLSearchParams({
      companyId: company.id, userId: currentUser.id, returnPage: 'meetings',
      ...(loginEmail ? { login_hint: loginEmail } : {}),
    });
    try { setWaking(true); await fetch(`${BACKEND_URL}/health`, { signal: AbortSignal.timeout(45000) }); }
    catch { /* cold start */ } finally { setWaking(false); }
    window.location.href = `${BACKEND_URL}/api/oauth/${provider}/start?${params.toString()}`;
  };

  const handleDisconnect = async (provider) => {
    await fetch(`${BACKEND_URL}/api/oauth/${provider}/disconnect`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId: company.id, userId: currentUser.id }),
    }).then(() => onRefresh());
  };

  const providers = [
    { key: 'google',    label: 'Google Meet',     icon: '🔴' },
    { key: 'microsoft', label: 'Microsoft Teams', icon: '🔵' },
    { key: 'zoom',      label: 'Zoom',            icon: '🟣' },
  ];

  const anyConnected = providers.some(p => oauthStatus?.[p.key]?.connected);

  return (
    <div style={{
      background: anyConnected ? 'rgba(16,185,129,0.05)' : 'rgba(124,58,237,0.04)',
      border: `1px solid ${anyConnected ? 'rgba(16,185,129,0.2)' : 'rgba(124,58,237,0.15)'}`,
      borderRadius: 14, padding: '14px 18px', marginBottom: 22,
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>
        🔗 Meeting Accounts
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
        {providers.map(p => {
          const conn = oauthStatus?.[p.key];
          const isConn = !!conn?.connected;
          return (
            <div key={p.key} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 30,
              background: isConn ? 'rgba(16,185,129,0.1)' : 'var(--bg-card)',
              border: `1px solid ${isConn ? 'rgba(16,185,129,0.25)' : 'var(--border)'}`,
            }}>
              <span style={{ fontSize: 13 }}>{p.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: isConn ? '#059669' : 'var(--text-secondary)' }}>
                {p.label}{isConn && conn.email ? ` · ${conn.email.split('@')[0]}` : ''}
              </span>
              {isConn ? (
                <button onClick={() => handleDisconnect(p.key)} style={{ background: 'none', border: 'none', fontSize: 9, color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: '0 2px' }}>✕</button>
              ) : (
                <button onClick={() => handleConnect(p.key)} disabled={!!connecting || waking} style={{
                  background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', border: 'none', color: '#fff',
                  fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '3px 10px', cursor: 'pointer', opacity: (connecting === p.key) ? 0.6 : 1,
                }}>
                  {waking && connecting === p.key ? '⏳' : connecting === p.key ? '…' : 'Connect'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {waking && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>⏳ Waking backend (~30s)…</span>}
    </div>
  );
}

// ─── Schedule / Edit Modal ────────────────────────────────────────────────────
function ScheduleModal({ meeting, leads, currentUser, company, oauthStatus, onRefreshOAuth, onClose, onSave }) {
  const { firebaseUser } = useAuth();
  const blank = {
    title: '', meetingType: 'Initial Consultation', platform: 'google_meet',
    date: '', time: '', duration: 30, leadId: '', leadName: '',
    meetLink: '', agenda: '', status: 'scheduled',
  };
  const [form, setForm] = useState(meeting ? { ...blank, ...meeting } : blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [waking, setWaking] = useState(false);

  const selectedLead = leads?.find(l => l.id === form.leadId);
  const platform = platformMap[form.platform] || platformMap.other;
  const requiredProvider = PLATFORM_TO_PROVIDER[form.platform];
  const isConnected = !!(requiredProvider && oauthStatus?.[requiredProvider]?.connected);
  const today = new Date().toISOString().split('T')[0];

  const handleConnect = async () => {
    setConnecting(true);
    const provId = requiredProvider === 'google' ? 'google.com' : 'microsoft.com';
    const loginEmail = firebaseUser?.providerData?.find(p => p.providerId === provId)?.email;
    const params = new URLSearchParams({
      companyId: company.id, userId: currentUser.id, returnPage: 'meetings',
      ...(loginEmail ? { login_hint: loginEmail } : {}),
    });
    try { setWaking(true); await fetch(`${BACKEND_URL}/health`, { signal: AbortSignal.timeout(45000) }); }
    catch { /* cold start */ } finally { setWaking(false); }
    window.location.href = `${BACKEND_URL}/api/oauth/${requiredProvider}/start?${params.toString()}`;
  };

  const handleCreateRealLink = async () => {
    if (!form.date) { setLinkError('Pick a date first.'); return; }
    setLinkError(''); setCreatingLink(true);
    try {
      const startTime = new Date(form.date + 'T' + (form.time || '09:00')).toISOString();
      const resp = await fetch(`${BACKEND_URL}/api/meetings/create-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company.id, userId: currentUser.id, platform: form.platform,
          title: form.title || form.meetingType, startTime, durationMinutes: form.duration,
          attendeeEmails: selectedLead?.email ? [selectedLead.email] : [],
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.success && data.link) {
        onSave({ ...form, meetLink: data.link });
      } else {
        setLinkError(data.error || 'Could not create meeting link.');
      }
    } catch { setLinkError('Could not reach the server.'); }
    setCreatingLink(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-primary)', borderRadius: 20, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.28)' }}>
        {/* Header */}
        <div style={{ padding: '22px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
              {meeting ? '✏️ Edit Meeting' : '📅 Schedule Meeting'}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '3px 0 0' }}>
              {meeting ? 'Update meeting details' : 'Book a session with a student'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Student */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelSt}>Student / Lead</label>
              <select className="crm-select" value={form.leadId} onChange={e => {
                const l = leads?.find(x => x.id === e.target.value);
                set('leadId', e.target.value); set('leadName', l?.name || '');
              }} style={{ width: '100%', marginTop: 4 }}>
                <option value="">— No lead selected —</option>
                {leads?.map(l => <option key={l.id} value={l.id}>{l.name}{l.phone ? ` · ${l.phone}` : ''}</option>)}
              </select>
            </div>

            {/* Title */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelSt}>Meeting Title</label>
              <input className="crm-input" value={form.title} onChange={e => set('title', e.target.value)}
                placeholder="e.g. Profile Review with Arjun" style={{ width: '100%', marginTop: 4 }} />
            </div>

            {/* Meeting type */}
            <div>
              <label style={labelSt}>Meeting Type</label>
              <select className="crm-select" value={form.meetingType} onChange={e => set('meetingType', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
                {MEETING_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>

            {/* Platform — now a clean dropdown */}
            <div>
              <label style={labelSt}>Platform</label>
              <select className="crm-select" value={form.platform} onChange={e => { set('platform', e.target.value); set('meetLink', ''); setLinkError(''); }}
                style={{ width: '100%', marginTop: 4 }}>
                {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.icon}  {p.label}</option>)}
              </select>
            </div>

            {/* Date */}
            <div>
              <label style={labelSt}>Date *</label>
              <input type="date" className="crm-input" min={today} value={form.date}
                onChange={e => set('date', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
            </div>

            {/* Time */}
            <div>
              <label style={labelSt}>Time</label>
              <input type="time" className="crm-input" value={form.time}
                onChange={e => set('time', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
            </div>

            {/* Duration */}
            <div>
              <label style={labelSt}>Duration</label>
              <select className="crm-select" value={form.duration} onChange={e => set('duration', Number(e.target.value))} style={{ width: '100%', marginTop: 4 }}>
                {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} minutes</option>)}
              </select>
            </div>

            {/* Status */}
            <div>
              <label style={labelSt}>Status</label>
              <select className="crm-select" value={form.status} onChange={e => set('status', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
                {Object.entries(MEETING_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {/* Meeting link */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelSt}>Meeting Link</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <input className="crm-input" value={form.meetLink} onChange={e => set('meetLink', e.target.value)}
                  placeholder={platform.autoLink ? 'Generate below or paste your own link' : 'Enter location or link (optional)'}
                  style={{ flex: 1 }} />
                {form.meetLink && (
                  <button onClick={() => navigator.clipboard?.writeText(form.meetLink)} type="button"
                    style={{ padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 14 }}>📋</button>
                )}
              </div>

              {/* Generation buttons */}
              {platform.autoLink && !form.meetLink && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {isConnected && (
                    <button onClick={handleCreateRealLink} disabled={creatingLink || !form.date} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10,
                      background: `linear-gradient(135deg, ${platform.color}, ${platform.color}cc)`,
                      color: '#fff', border: 'none', cursor: (!form.date || creatingLink) ? 'not-allowed' : 'pointer',
                      fontSize: 12, fontWeight: 700, opacity: !form.date ? 0.5 : 1,
                    }}>
                      {creatingLink ? '⏳ Creating…' : `${platform.icon} Create Real ${platform.label} Link`}
                    </button>
                  )}
                  <button onClick={() => set('meetLink', generateMeetLink())} type="button" style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10,
                    background: isConnected ? 'transparent' : `linear-gradient(135deg, ${platform.color}22, ${platform.color}44)`,
                    color: platform.color, border: `1px solid ${platform.color}40`, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  }}>⚡ {isConnected ? 'Quick Jitsi Link' : 'Generate Instant Link'}</button>
                </div>
              )}

              {form.meetLink && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>✅ Link ready</span>
                  <button onClick={() => { set('meetLink', ''); setLinkError(''); }} type="button"
                    style={{ background: 'none', border: 'none', fontSize: 11, color: '#ef4444', cursor: 'pointer' }}>regenerate</button>
                </div>
              )}

              {linkError && <div style={{ marginTop: 6, fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⚠ {linkError}</div>}

              {/* Connection status pill */}
              {platform.autoLink && requiredProvider && (
                <div style={{ marginTop: 10 }}>
                  {isConnected ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 30, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                      <span style={{ fontSize: 11 }}>✅</span>
                      <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>
                        {PROVIDER_LABEL[requiredProvider]} connected{oauthStatus?.[requiredProvider]?.email ? ` · ${oauthStatus[requiredProvider].email}` : ''}
                      </span>
                    </div>
                  ) : (
                    <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.6 }}>
                        Connect your <strong>{PROVIDER_LABEL[requiredProvider]}</strong> account to auto-generate real {platform.label} links.
                      </div>
                      <button onClick={handleConnect} disabled={connecting || waking} style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', fontSize: 12, fontWeight: 700,
                        opacity: (connecting || waking) ? 0.7 : 1,
                      }}>{waking ? '⏳ Starting up…' : connecting ? '⏳ Redirecting…' : `🔗 Connect ${PROVIDER_LABEL[requiredProvider]}`}</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Agenda */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelSt}>Agenda / Notes</label>
              <textarea className="crm-input" value={form.agenda} onChange={e => set('agenda', e.target.value)}
                rows={3} placeholder="Topics to discuss in this meeting…" style={{ width: '100%', marginTop: 4, resize: 'vertical' }} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
            <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>Cancel</button>
            <button onClick={() => onSave(form)} disabled={!form.date} className="btn-primary" style={{ padding: '10px 24px', fontWeight: 700, opacity: !form.date ? 0.5 : 1 }}>
              {meeting ? '💾 Save Changes' : '📅 Schedule Meeting'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Meeting Card ─────────────────────────────────────────────────────────────
function MeetingCard({ meeting, onEdit, onDelete, onStatusChange, leads }) {
  const p = platformMap[meeting.platform] || platformMap.other;
  const st = MEETING_STATUS[meeting.status] || MEETING_STATUS.scheduled;
  const lead = leads?.find(l => l.id === meeting.leadId);
  const mins = minutesUntil(meeting.date, meeting.time);
  const now = Date.now();
  const startMs = meeting.date && meeting.time ? new Date(meeting.date + 'T' + meeting.time).getTime() : null;
  const isNow = startMs && now >= startMs && now <= startMs + (meeting.duration || 30) * 60000;
  const upcoming = startMs && startMs > now;

  return (
    <div style={{
      background: 'var(--bg-card)', border: `1px solid ${isNow ? 'rgba(16,185,129,0.4)' : 'var(--border)'}`,
      borderRadius: 16, padding: '16px 18px',
      boxShadow: isNow ? '0 0 0 3px rgba(16,185,129,0.1), var(--shadow-card)' : 'var(--shadow-card)',
      borderLeft: `4px solid ${p.color}`, position: 'relative', overflow: 'hidden',
    }}>
      {isNow && (
        <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, fontWeight: 800, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20 }}>
          ● LIVE NOW
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${p.color}20, ${p.color}0d)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          border: `1px solid ${p.color}30`,
        }}>{p.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: 2 }}>
            {meeting.title || meeting.meetingType || 'Meeting'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {p.label} · {meeting.duration || 30} min
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button onClick={() => onEdit(meeting)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', padding: 4, borderRadius: 6 }}>✏️</button>
          <button onClick={() => onDelete(meeting.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#ef4444', padding: 4, borderRadius: 6 }}>🗑</button>
        </div>
      </div>

      {/* Lead chip */}
      {(lead || meeting.leadName) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '5px 10px', background: 'rgba(124,58,237,0.06)', borderRadius: 8, border: '1px solid rgba(124,58,237,0.1)' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 800, flexShrink: 0 }}>
            {(lead?.name || meeting.leadName || '?')[0].toUpperCase()}
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>{lead?.name || meeting.leadName}</span>
          {(lead?.phone || meeting.leadPhone) && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>· {lead?.phone || meeting.leadPhone}</span>}
        </div>
      )}

      {/* Date/time */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
        📅 {formatDT(meeting.date, meeting.time)}
      </div>

      {/* Countdown */}
      {upcoming && mins !== null && meeting.status === 'scheduled' && (
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: mins <= 60 ? '#ef4444' : mins <= 1440 ? '#f59e0b' : '#10b981' }}>
          {mins <= 0 ? '🔴 Starting now!' : mins < 60 ? `⚡ In ${mins} min` : mins < 1440 ? `⏰ In ${Math.ceil(mins / 60)}h` : `📅 In ${Math.ceil(mins / 1440)} days`}
        </div>
      )}

      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: st.color + '15', color: st.color, fontSize: 11, fontWeight: 700 }}>
          {st.label}
        </span>
        <select className="crm-select" value={meeting.status} onChange={e => onStatusChange(meeting.id, e.target.value)} style={{ fontSize: 11, padding: '3px 8px' }}>
          {Object.entries(MEETING_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Join link — provider-branded */}
      {meeting.meetLink && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
          <a href={meeting.meetLink} target="_blank" rel="noopener noreferrer" style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '10px', background: `linear-gradient(135deg, ${p.color}22, ${p.color}0d)`,
            borderRadius: 9, textDecoration: 'none', color: p.color, fontSize: 12, fontWeight: 800,
            border: `1px solid ${p.color}40`, letterSpacing: 0.1,
          }}>
            {meeting.platform === 'google_meet' ? '🔴 Join Google Meet' :
             meeting.platform === 'teams'       ? '🟦 Join Teams'       :
             meeting.platform === 'zoom'        ? '🔵 Join Zoom'        : '🔗 Join Meeting'}
          </a>
          <button onClick={() => navigator.clipboard?.writeText(meeting.meetLink)} style={{
            padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)',
          }}>📋</button>
          {(lead?.phone || meeting.leadPhone) && (
            <a href={`https://wa.me/${(lead?.phone || meeting.leadPhone || '').replace(/\D/g, '')}?text=${encodeURIComponent('Meeting link: ' + meeting.meetLink)}`}
              target="_blank" rel="noopener noreferrer" style={{
                padding: '9px 12px', borderRadius: 9, border: '1px solid rgba(37,211,102,0.3)', background: 'rgba(37,211,102,0.08)', color: '#25d366', fontSize: 12, fontWeight: 700, textDecoration: 'none',
              }}>📱</a>
          )}
        </div>
      )}

      {/* Agenda */}
      {meeting.agenda && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)', padding: '8px 10px', background: 'var(--bg-secondary)', borderRadius: 8, borderLeft: '3px solid var(--border)', lineHeight: 1.6 }}>
          {meeting.agenda}
        </div>
      )}
    </div>
  );
}

// ─── Mini Calendar ────────────────────────────────────────────────────────────
function CalendarMini({ meetings, selectedDate, onSelectDate }) {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year = viewMonth.getFullYear(); const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const meetDates = new Set(meetings.map(m => m.date).filter(Boolean));
  const days = []; for (let i = 0; i < firstDay; i++) days.push(null); for (let d = 1; d <= daysInMonth; d++) days.push(d);
  const pad = n => String(n).padStart(2, '0'); const ds = d => `${year}-${pad(month + 1)}-${pad(d)}`;
  const todayStr = today.toISOString().split('T')[0];
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={() => setViewMonth(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-secondary)', padding: 4 }}>‹</button>
        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {viewMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
        </span>
        <button onClick={() => setViewMonth(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-secondary)', padding: 4 }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center' }}>
        {['S','M','T','W','T','F','S'].map((d, i) => <div key={i} style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', padding: '2px 0' }}>{d}</div>)}
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const dss = ds(d); const hasMeet = meetDates.has(dss); const isToday = dss === todayStr; const isSel = dss === selectedDate;
          return (
            <div key={i} onClick={() => onSelectDate(isSel ? null : dss)} style={{
              padding: '5px 2px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: isSel ? '#7c3aed' : isToday ? 'rgba(124,58,237,0.1)' : 'transparent',
              color: isSel ? '#fff' : isToday ? '#7c3aed' : 'var(--text-primary)', position: 'relative',
            }}>
              {d}
              {hasMeet && !isSel && <div style={{ width: 4, height: 4, background: '#7c3aed', borderRadius: '50%', position: 'absolute', bottom: 1, left: '50%', transform: 'translateX(-50%)' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Meetings Page ───────────────────────────────────────────────────────
export default function MeetingsPage() {
  const { currentUser, company, leads, setCurrentPage } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [tab, setTab] = useState('upcoming');
  const [syncedEvents, setSyncedEvents] = useState([]);
  const [calSyncing, setCalSyncing] = useState(false);
  const [banner, setBanner] = useState('');

  const [oauthStatus, refreshOAuth] = useOAuthStatus(company?.id, currentUser?.id);

  const visibleLeads = useMemo(() => {
    if (!leads) return [];
    if (isAdmin) return leads;
    return leads.filter(l => l.assignedTo === currentUser?.id || l.assignedToId === currentUser?.id);
  }, [leads, isAdmin, currentUser]);

  // Load CRM meetings
  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'companies', company.id, 'meetings'),
      snap => {
        let items = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        if (!isAdmin) items = items.filter(m => m.createdBy === currentUser?.id || visibleLeads.some(l => l.id === m.leadId));
        items.sort((a, b) => (a.date + (a.time || '')).localeCompare(b.date + (b.time || '')));
        setMeetings(items); setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, [company?.id, isAdmin, currentUser?.id]); // eslint-disable-line

  // Subscribe to synced work calendar events
  useEffect(() => {
    if (!company?.id || !currentUser?.id) return;
    const q = query(collection(db, 'companies', company.id, 'syncedMeetings'), where('userId', '==', currentUser.id));
    return onSnapshot(q, snap => {
      const now = Date.now();
      setSyncedEvents(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(e => e.start && e.start >= now - 14 * 24 * 60 * 60 * 1000)
          .sort((a, b) => (a.start || 0) - (b.start || 0))
      );
    }, () => {});
  }, [company?.id, currentUser?.id]); // eslint-disable-line

  // Detect return from OAuth (?connected= param set by backend callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      setBanner(connected);
      refreshOAuth();
      params.delete('connected'); params.delete('oauthError');
      window.history.replaceState({}, '', window.location.pathname + (params.toString() ? '?' + params : ''));
    }
  }, []); // eslint-disable-line

  const handleSyncCalendar = async () => {
    if (!company?.id || !currentUser?.id || calSyncing) return;
    setCalSyncing(true);
    try {
      await fetch(`${BACKEND_URL}/api/work-sync/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId: company.id, userId: currentUser.id, days: 14 }),
      });
    } catch { /* ignore */ } finally { setCalSyncing(false); }
  };

  const handleSave = async (form) => {
    try {
      const { id, ...data } = form;
      if (id) {
        await updateDoc(doc(db, 'companies', company.id, 'meetings', id), { ...data, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, 'companies', company.id, 'meetings'), {
          ...data, createdBy: currentUser?.id, createdByName: currentUser?.name,
          companyId: company.id, createdAt: serverTimestamp(),
        });
      }
    } catch (e) { console.error(e); }
    setScheduleModal(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    await deleteDoc(doc(db, 'companies', company.id, 'meetings', id));
  };

  const handleStatusChange = async (id, status) => {
    await updateDoc(doc(db, 'companies', company.id, 'meetings', id), { status, updatedAt: serverTimestamp() });
  };

  const filtered = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return meetings.filter(m => {
      if (tab === 'upcoming' && (m.date < today || m.status === 'cancelled')) return false;
      if (tab === 'past' && m.date >= today) return false;
      if (filterStatus && m.status !== filterStatus) return false;
      if (filterPlatform && m.platform !== filterPlatform) return false;
      if (selectedDate && m.date !== selectedDate) return false;
      return true;
    });
  }, [meetings, tab, filterStatus, filterPlatform, selectedDate]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return {
      today: meetings.filter(m => m.date === today && m.status === 'scheduled').length,
      upcoming: meetings.filter(m => m.date > today && m.status === 'scheduled').length,
      completed: meetings.filter(m => m.status === 'completed').length,
      total: meetings.length,
    };
  }, [meetings]);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1300, margin: '0 auto', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>

      {/* Success banner */}
      {banner && (
        <div style={{ marginBottom: 16, padding: '12px 18px', borderRadius: 12, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🎉</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
            {PROVIDER_LABEL[banner] || banner} account connected! You can now generate real meeting links.
          </span>
          <button onClick={() => setBanner('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#059669', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.5px' }}>📅 Meetings</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Schedule Google Meet, Teams, Zoom and in-person sessions with students
          </p>
        </div>
        <button onClick={() => setScheduleModal({})} className="btn-primary" style={{ fontSize: 13, fontWeight: 700, padding: '10px 20px' }}>
          + Schedule Meeting
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Today', val: stats.today, icon: '☀️', color: '#f59e0b', bg: 'rgba(245,158,11,0.07)' },
          { label: 'Upcoming', val: stats.upcoming, icon: '📅', color: '#0ea5e9', bg: 'rgba(14,165,233,0.07)' },
          { label: 'Completed', val: stats.completed, icon: '✅', color: '#10b981', bg: 'rgba(16,185,129,0.07)' },
          { label: 'Total', val: stats.total, icon: '📊', color: '#7c3aed', bg: 'rgba(124,58,237,0.07)' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}20`, borderRadius: 14, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>{s.icon}</span>
              <span style={{ fontSize: 10, fontWeight: 800, color: s.color, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Connected accounts bar */}
      <ConnectedAccountsBar currentUser={currentUser} company={company} oauthStatus={oauthStatus} onRefresh={refreshOAuth} />

      {/* Layout */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* Left: Filters */}
        <div style={{ width: 220, flexShrink: 0 }}>
          <CalendarMini meetings={meetings} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
          {selectedDate && (
            <button onClick={() => setSelectedDate(null)} style={{ width: '100%', marginBottom: 10, padding: '7px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.3)', background: 'none', cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 700 }}>
              ✕ Clear date filter
            </button>
          )}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 5 }}>Platform</div>
            <select className="crm-select" value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} style={{ width: '100%', fontSize: 12 }}>
              <option value="">All Platforms</option>
              {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 5 }}>Status</div>
            <select className="crm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: '100%', fontSize: 12 }}>
              <option value="">All Statuses</option>
              {Object.entries(MEETING_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        {/* Right: Meeting list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 2, marginBottom: 18, background: 'var(--bg-card)', borderRadius: 10, padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
            {[
              { id: 'upcoming', label: 'Upcoming' },
              { id: 'past', label: 'Past' },
              { id: 'all', label: 'All' },
              { id: 'calendar', label: `📆 Work Calendar${syncedEvents.length ? ` (${syncedEvents.length})` : ''}` },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '7px 16px', borderRadius: 8, cursor: 'pointer', border: 'none', fontSize: 12, fontWeight: 700,
                background: tab === t.id ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'transparent',
                color: tab === t.id ? '#fff' : 'var(--text-secondary)',
                boxShadow: tab === t.id ? '0 2px 8px rgba(124,58,237,0.28)' : 'none', transition: 'all 0.15s',
              }}>{t.label}</button>
            ))}
          </div>

          {/* Work Calendar tab */}
          {tab === 'calendar' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)' }}>Events from your connected Google / Microsoft work calendar.</div>
                <button onClick={handleSyncCalendar} disabled={calSyncing} style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.06)',
                  color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: calSyncing ? 'default' : 'pointer', opacity: calSyncing ? 0.6 : 1,
                }}>{calSyncing ? '⏳ Syncing…' : '🔄 Sync Now'}</button>
              </div>
              {syncedEvents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 44, display: 'block', marginBottom: 10 }}>📆</span>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>No work calendar events synced</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Connect your Google or Microsoft account above, then click Sync Now.</div>
                  <button onClick={handleSyncCalendar} disabled={calSyncing} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', fontSize: 13, fontWeight: 700 }}>🔄 Sync Now</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {syncedEvents.map(ev => {
                    const startDate = ev.start ? new Date(ev.start) : null;
                    const endDate = ev.end ? new Date(ev.end) : null;
                    const isNow_ = startDate && endDate && Date.now() >= ev.start && Date.now() <= ev.end;
                    const isPast = endDate && Date.now() > ev.end;
                    const pColor = PROVIDER_COLOR[ev.provider] || '#7c3aed';
                    return (
                      <div key={ev.id} style={{
                        background: 'var(--bg-card)', borderRadius: 12, padding: '14px 16px',
                        display: 'flex', gap: 14, alignItems: 'flex-start', opacity: isPast ? 0.6 : 1,
                        borderLeft: `4px solid ${pColor}`, border: `1px solid ${isNow_ ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
                      }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: pColor + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>
                          {ev.provider === 'google' ? '🔴' : ev.provider === 'microsoft' ? '🔵' : '🟣'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>{ev.title}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {startDate && startDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + startDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            {endDate && ` – ${endDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                            {ev.durationMinutes ? ` · ${ev.durationMinutes}min` : ''}
                          </div>
                          {ev.attendeeCount > 1 && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>👥 {ev.attendeeCount} attendees</div>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                          {isNow_ && <span style={{ fontSize: 10, fontWeight: 800, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 20 }}>● LIVE</span>}
                          {ev.link && (
                            <a href={ev.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: pColor, fontWeight: 700, textDecoration: 'none', padding: '4px 10px', borderRadius: 6, border: `1px solid ${pColor}30`, background: pColor + '10' }}>Join →</a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CRM meeting cards */}
          {tab !== 'calendar' && (
            loading ? (
              <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary)', fontSize: 13 }}>Loading meetings…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 44, display: 'block', marginBottom: 10 }}>📅</span>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>No meetings found</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  {tab === 'upcoming' ? 'No upcoming meetings scheduled yet.' : 'No meetings match your filters.'}
                </div>
                <button onClick={() => setScheduleModal({})} className="btn-primary" style={{ fontSize: 13 }}>+ Schedule Meeting</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                {filtered.map(m => (
                  <MeetingCard key={m.id} meeting={m} leads={leads} onEdit={m => setScheduleModal(m)} onDelete={handleDelete} onStatusChange={handleStatusChange} />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {scheduleModal !== null && (
        <ScheduleModal
          meeting={scheduleModal?.id ? scheduleModal : null}
          leads={visibleLeads}
          currentUser={currentUser}
          company={company}
          oauthStatus={oauthStatus}
          onRefreshOAuth={refreshOAuth}
          onClose={() => setScheduleModal(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
